/**
 * Phone Number Utilities for WhatsApp (Argentine Mobile / International)
 * 
 * Target format for WhatsApp AR: 549 + Area Code (without 0) + Number (without 15)
 * Example: 5491178280466 (13 digits total)
 */

/**
 * Clean raw input string removing non-digits (preserves leading + internally for analysis)
 */
export const normalizePhone = (rawPhone) => {
  if (!rawPhone || typeof rawPhone !== 'string') return '';

  let cleaned = rawPhone.trim();

  // If input contains letters or invalid characters, reject
  if (/[a-zA-Z]/.test(cleaned)) {
    return '';
  }

  // Handle repeated +54+54 or +5454
  cleaned = cleaned.replace(/(\+?54)+/g, '+54');

  // Remove all non-digits
  let digitsOnly = cleaned.replace(/\D/g, '');

  if (!digitsOnly || digitsOnly.length < 6) return '';

  // Case 1: Already starts with 549 and has 13 digits (e.g. 5491178280466) -> perfect
  if (digitsOnly.startsWith('549') && digitsOnly.length === 13) {
    return digitsOnly;
  }

  // Case 2: Starts with 54 (without 9) and has 12 digits (e.g. 541178280466 -> needs 9)
  if (digitsOnly.startsWith('54') && !digitsOnly.startsWith('549') && digitsOnly.length === 12) {
    return '549' + digitsOnly.slice(2);
  }

  // Case 3: Starts with 5415 or 54915 or 15
  if (digitsOnly.startsWith('54')) {
    digitsOnly = digitsOnly.slice(2); // Remove country code 54 first
  }
  if (digitsOnly.startsWith('9')) {
    digitsOnly = digitsOnly.slice(1); // Remove 9 temporarily
  }
  if (digitsOnly.startsWith('0')) {
    digitsOnly = digitsOnly.slice(1); // Remove leading 0 (e.g. 011 -> 11)
  }

  // Now digitsOnly is local part (area code + number)
  if (digitsOnly.startsWith('1115')) {
    digitsOnly = '11' + digitsOnly.slice(4);
  } else if (/^\d{2,4}15\d{6,8}$/.test(digitsOnly)) {
    digitsOnly = digitsOnly.replace(/(\d{2,4})15(\d{6,8})/, '$1$2');
  }

  // If local part has 10 digits (e.g., 1131448824 or 3511234567), prepend 549
  if (digitsOnly.length === 10) {
    return '549' + digitsOnly;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith('9')) {
    return '54' + digitsOnly;
  }

  if (digitsOnly.length >= 10 && digitsOnly.length <= 11) {
    return '549' + (digitsOnly.startsWith('9') ? digitsOnly.slice(1) : digitsOnly);
  }

  return digitsOnly;
};

/**
 * Validates whether normalized phone string is a valid WhatsApp target (specifically 11-15 digits starting with country code)
 */
export const isValidMobilePhone = (rawPhone) => {
  const normalized = normalizePhone(rawPhone);
  if (!normalized) return false;
  return /^\d{11,15}$/.test(normalized);
};

/**
 * Format for display (e.g. +54 9 11 7828-0466)
 */
export const formatPhoneDisplay = (rawPhone) => {
  const normalized = normalizePhone(rawPhone);
  if (!normalized) return rawPhone || '';

  if (normalized.startsWith('549') && normalized.length === 13) {
    const area = normalized.slice(3, 5); // 11
    const part1 = normalized.slice(5, 9); // 7828
    const part2 = normalized.slice(9);    // 0466
    return `+54 9 ${area} ${part1}-${part2}`;
  }

  return `+${normalized}`;
};
